import { Station } from '../../../../types/azk-binding.types';

export const addStationMarker = (
  station: Station,
  color: string,
  bounds: any,
  map: any,
  infoWindowRef: React.MutableRefObject<any>,
  showError: (msg: string) => void
) => {
  const lat = Number(station.lat || station.latitude || 0);
  const lng = Number(station.lng || station.longitude || 0);

  if (isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) {
    if (!station.lat && !station.latitude && !station.lng && !station.longitude) {
      showError(`Станція ${station.station_number || station.unicode} не має координат для відображення на карті`);
    }
    return null;
  }

  // Format address for hover tooltip according to client specification
  // "{brand}, {settlement_type}{'.', if settlement_type != 'селище' else ''}, {settlement_name}, {address}"
  const formatAddress = () => {
    const parts = [];
    
    if (station.brand) {
      parts.push(station.brand);
    }
    
    if (station.settlement_type) {
      const settlementPart = station.settlement_type === 'селище' 
        ? station.settlement_type 
        : station.settlement_type + '.';
      parts.push(settlementPart);
    }
    
    if (station.settlement_name) {
      parts.push(station.settlement_name);
    }
    
    if (station.address) {
      parts.push(station.address);
    }
    
    return parts.join(', ');
  };
  
  const formattedAddress = formatAddress();

  let marker;
  let hoverInfoWindow: any = null;
  
  try {
    if (window.google.maps.marker?.AdvancedMarkerElement) {
      const pinElement = document.createElement('div');
      pinElement.innerHTML = `
        <svg width="24" height="36" viewBox="0 0 24 36" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 0C5.373 0 0 5.373 0 12c0 9 12 24 12 24s12-15 12-24c0-6.627-5.373-12-12-12z" fill="${color}"/>
          <circle cx="12" cy="12" r="6" fill="white"/>
        </svg>
      `;
      pinElement.style.cursor = 'pointer';
      
      marker = new window.google.maps.marker.AdvancedMarkerElement({
        position: { lat, lng },
        map: map,
        title: formattedAddress,
        content: pinElement
      });
    } else {
      throw new Error('AdvancedMarkerElement not available');
    }
  } catch {
    marker = new window.google.maps.Marker({
      position: { lat, lng },
      map: map,
      title: formattedAddress,
      icon: {
        path: `M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z M -5,-30 A 5,5 0 1,0 5,-30 A 5,5 0 1,0 -5,-30 z`,
        scale: 1,
        fillColor: color,
        fillOpacity: 1,
        strokeColor: color,
        strokeWeight: 0,
        anchor: new window.google.maps.Point(0, 0)
      }
    });
  }

  const hasPosition = 'position' in marker;
  const clickEvent = hasPosition ? 'gmp-click' : 'click';
  const mouseOverEvent = hasPosition ? 'gmp-mouseover' : 'mouseover';
  const mouseOutEvent = hasPosition ? 'gmp-mouseout' : 'mouseout';
  
  // Hover functionality - show address on mouseover
  marker.addListener(mouseOverEvent, () => {
    if (hoverInfoWindow) {
      hoverInfoWindow.close();
    }
    
    hoverInfoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 4px 8px; font-size: 12px; max-width: 250px;">
          <div style="color: #666;">${formattedAddress}</div>
        </div>
      `,
      disableAutoPan: true
    });
    
    if (hasPosition) {
      hoverInfoWindow.open({
        anchor: marker,
        map
      });
    } else {
      hoverInfoWindow.open(map, marker);
    }
  });
  
  // Hide hover tooltip on mouseout
  marker.addListener(mouseOutEvent, () => {
    if (hoverInfoWindow) {
      hoverInfoWindow.close();
      hoverInfoWindow = null;
    }
  });
  
  // Click functionality - show detailed info
  marker.addListener(clickEvent, () => {
    // Close hover tooltip if open
    if (hoverInfoWindow) {
      hoverInfoWindow.close();
      hoverInfoWindow = null;
    }
    
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }
    
    const newInfoWindow = new window.google.maps.InfoWindow({
      headerContent: `${station.brand || 'АЗС'} - ${station.station_number}`,
      content: `
        <div style="padding: 8px; min-width: 200px;">
          <div style="margin-bottom: 4px;">${station.address}</div>
          <div style="color: #666; font-size: 12px;">${station.region || ''} ${station.province || ''}</div>
        </div>
      `
    });
    
    if (hasPosition) {
      newInfoWindow.open({
        anchor: marker,
        map
      });
    } else {
      newInfoWindow.open(map, marker);
    }
    
    infoWindowRef.current = newInfoWindow;
  });

  if (hasPosition) {
    bounds.extend((marker as any).position);
  } else if ((marker as any).getPosition) {
    bounds.extend((marker as any).getPosition());
  }
  return marker;
};

export const drawLineBetweenStations = (station1: Station, station2: Station, map: any) => {
  const lat1 = Number(station1.lat || station1.latitude || 0);
  const lng1 = Number(station1.lng || station1.longitude || 0);
  const lat2 = Number(station2.lat || station2.latitude || 0);
  const lng2 = Number(station2.lng || station2.longitude || 0);

  if (isNaN(lat1) || isNaN(lng1) || isNaN(lat2) || isNaN(lng2) || 
      lat1 === 0 || lng1 === 0 || lat2 === 0 || lng2 === 0) {
    return null;
  }

  const line = new window.google.maps.Polyline({
    path: [
      { lat: lat1, lng: lng1 },
      { lat: lat2, lng: lng2 }
    ],
    geodesic: true,
    strokeColor: '#2196F3',
    strokeOpacity: 0.8,
    strokeWeight: 2
  });

  line.setMap(map);
  return line;
};
